import BaseApi, { BaseApiResponse } from '@lumapps/base-api';
import { sanitizeNavigationMapForUpdate } from '@lumapps/navigation-admin/utils';
import { Navigation, NavigationMap } from '@lumapps/navigation/types';
import { withApiv1Languages } from '@lumapps/translations';

const getSpaceNavigationRootPath = (instanceId: string, spaceId: string, urlEnd: string) =>
    `sites/${instanceId}/community/${spaceId}/${urlEnd}`;

export const spaceNavigationApi = new BaseApi({
    version: BaseApi.versions.v2,
    // force direct call to Haussmann navigation service instead of passing it through GAE
    bypassMonolith: true,
});

/**
 * @function getSpaceNavigation
 * @param instanceId the instance/site the space belongs to
 * @param spaceId the space id
 * @param acceptLanguage the Accept-Language header
 *
 * Gets the navigation of a space
 */
export const getSpaceNavigation = async (
    instanceId: string,
    spaceId: string,
    acceptLanguage: string,
): Promise<BaseApiResponse<Navigation>> =>
    spaceNavigationApi.get(getSpaceNavigationRootPath(instanceId, spaceId, 'map/navigations/community-navigation'), {
        headers: {
            'Accept-Language': acceptLanguage,
        },
    });

/**
 * @function getSpaceNavigationMap
 * @param instanceId the instance/site the space belongs to
 * @param spaceId the space id
 * @param acceptLanguage the Accept-Language header
 *
 * Gets the navigation-map which is used in the space navigation administration
 */
export const getSpaceNavigationMap = async (
    instanceId: string,
    spaceId: string,
    acceptLanguage: string,
): Promise<BaseApiResponse<NavigationMap>> => {
    const { data, ...response } = await spaceNavigationApi.get(
        getSpaceNavigationRootPath(instanceId, spaceId, 'navigation-maps/community'),
        {
            headers: {
                'Accept-Language': acceptLanguage,
            },
        },
    );

    /**
     * When regional languages (e.g. pt-BR, zh-TW) are set, make sure to convert them into v1 format
     * (pt_br, zh_tw) so it works with the multilang components and the rest of app
     */
    return { ...response, data: withApiv1Languages(data) };
};

/**
 * @function putSpaceNavigationMap
 * @param instanceId the instance/site the space belongs to
 * @param spaceId the space id
 * @param navigationMap the new navigationMap
 * @param acceptLanguage the Accept-Language header
 *
 * Updates the navigationMap: takes the whole navigationMap and sanitizes it before sending it to the API.
 * The endpoint will return the updated navigationMap
 */
export const putSpaceNavigationMap = async (
    instanceId: string,
    spaceId: string,
    navigationMap: NavigationMap,
    acceptLanguage: string,
): Promise<BaseApiResponse<NavigationMap>> => {
    const { data, ...response } = await spaceNavigationApi.put(
        getSpaceNavigationRootPath(instanceId, spaceId, 'navigation-maps/community'),
        sanitizeNavigationMapForUpdate(navigationMap, { removeEmptySectionChildren: true }),
        {
            headers: {
                'Accept-Language': acceptLanguage,
            },
        },
    );

    /**
     * When regional languages (e.g. pt-BR, zh-TW) are set, make sure to convert them into v1 format
     * (pt_br, zh_tw) so it works with the multilang components and the rest of app
     */
    return { ...response, data: withApiv1Languages(data) };
};

/**
 * @function duplicateSpaceNavigationMap
 * @param instanceId the instance/site the space belongs to
 * @param spaceId the space id
 * @param destinationSpaceId the new space id
 * @param acceptLanguageHeader the Accept-Language header
 *
 * Duplicate navigation maps for a space duplication
 */
export const duplicateSpaceNavigationMap = async (
    instanceId: string,
    spaceId: string,
    destinationSpaceId: string,
    acceptLanguageHeader: string,
): Promise<BaseApiResponse> =>
    spaceNavigationApi.post(
        getSpaceNavigationRootPath(instanceId, spaceId, 'navigation-maps/community/duplicate'),
        { destinationCommunityId: destinationSpaceId },
        { headers: { 'Accept-Language': acceptLanguageHeader } },
    );

export const queryKeys = {
    /** Prefix matching every space-navigation query, regardless of instance/space/language. */
    all: () => ['spaceNavigation'] as const,
    getSpaceNavigation: (instanceId: string, spaceId: string, acceptLanguageHeader: string) => [
        ...queryKeys.all(),
        instanceId,
        spaceId,
        acceptLanguageHeader,
    ],
};
